import { Attrs, EventListeners, MinzeElement } from 'minze'
import { Font, MODALS, SCREEN_WIDTHS } from '../../../constants'
import { Callback } from '../../../modules/callback'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import {
  AbstractSearchCore,
  AbstractSubscriber,
  IFacet,
  IFacetOption,
  ISearchConfig,
} from '../../../modules/search'
import { getVisibleWidth } from '../../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'audiencesFilter', {
  audience: 'Auditoires',
  selectedAudiences: 'Auditoires sélectionnées',
  results: 'Résultats',
  audiences: {
    general: 'Général',
    toddler: 'Tout-Petit',
    children: 'Enfant',
    teen: 'Adolescent',
    adult: 'Adulte',
    senior: 'Aîné',
    family: 'Familial',
  },
})

Locale.getInstance().registerKeySet('en', 'audiencesFilter', {
  audience: 'Audiences',
  selectedAudiences: 'Selected audiences',
  results: 'Results',
  audiences: {
    general: 'General',
    toddler: 'Toddler',
    children: 'Children',
    teen: 'Teen',
    adult: 'Adult',
    senior: 'Senior',
    family: 'Family',
  },
})

class LvcAudiencesSubscriber extends AbstractSubscriber {
  private _core: AbstractSearchCore | null = null
  private _callback: (...args: any[]) => void

  constructor(groupId: string, callback: (...args: any[]) => void) {
    super(groupId)
    this._callback = callback
  }

  public async attached(core: AbstractSearchCore): Promise<void> {
    this._core = core
  }

  public detaching(): void {
    this._core = null
  }

  async onSearchTextUpdate(text: string): Promise<void> {}
  async onResultUpdate(results: any): Promise<void> {}
  async onFacetUpdate(facets: IFacet[]): Promise<void> {}

  async onSearchConfigUpdate(config: ISearchConfig): Promise<void> {
    let audienceFacet = config?.facets?.find(facet => facet.name === 'audience')

    if (audienceFacet) {
      let options = []
      for (let option of audienceFacet.options) {
        if (option.selected) {
          options.push(facetToOption(option))
        }
      }
      //keep unique values
      options = options.filter((value, index, self) => self.indexOf(value) === index)

      this._callback(options)
    }
  }

  public async setAudienceFilter(audiences: string[]): Promise<void> {
    await this.whenReady()

    let config = this._core?.searchConfig || {
      facets: [{ name: 'audience', options: [] }],
    }
    if (!config.facets) {
      config.facets = [{ name: 'audience', options: [] }]
    }

    let audienceFacet = config.facets.find(facet => facet.name === 'audience')

    if (!audienceFacet) {
      audienceFacet = { name: 'audience', options: [] }
    }

    let options = []
    for (let audience of audiences) {
      options.push(...optionToFacet(audience))
    }

    audienceFacet.options = options

    let index = config.facets.findIndex(facet => facet.name === 'audience')
    if (index >= 0) {
      config.facets[index] = audienceFacet
    } else {
      config.facets.push(audienceFacet)
    }
    this._core?.applySearchConfig(config)
  }

  async whenReady(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this._core) {
        resolve(true)
      } else {
        let interval = setInterval(() => {
          if (this._core) {
            clearInterval(interval)
            resolve(true)
          }
        }, 10)
      }
    })
  }
}

//=============================================================================
//=============================================================================
//=============================================================================
export interface LvcAudiencesSearch {
  dataGroupId?: string
}
export class LvcAudiencesSearch extends MinzeElement {
  attrs: Attrs = ['data-group-id']
  private clickCb: string = ''
  private clearCb: string = ''
  private toggleCb: string = ''
  private drawerToggleCb: string = ''
  private drawerClickCb: string = ''
  private selectedAudiences: string[] = []
  private subscriber: LvcAudiencesSubscriber | null = null

  async onReady(): Promise<void> {
    this.clickCb = Callback.attach(this.onToggleButtonClick.bind(this))
    this.clearCb = Callback.attach(this.onClearClick.bind(this))
    this.toggleCb = Callback.attach(this.onToggle.bind(this))
    this.drawerToggleCb = Callback.attach(this.onDrawerToggle.bind(this))
    this.drawerClickCb = Callback.attach(this.onDrawerClick.bind(this))
    this.subscriber = new LvcAudiencesSubscriber(
      this.groupId as string,
      this.onAudiencesChange.bind(this)
    )
    MainModule.getInstance().getSearchManager().subscribe(this.subscriber)
    this.rerender()
  }

  onDestroy() {
    Callback.detach(this.clickCb)
    Callback.detach(this.clearCb)
    Callback.detach(this.toggleCb)
    Callback.detach(this.drawerToggleCb)
    Callback.detach(this.drawerClickCb)
  }

  onToggle(state: boolean): void {
    this.isOpen = state
    this.rerender()
  }

  onToggleButtonClick = () => {
    MainModule.getInstance().showModal(MODALS.AUDIENCE_DROPDOWN)
    this.rerender()
  }

  onDrawerToggle = () => {
    this.isOpen = !this.isOpen
    this.rerender()
  }

  onDrawerClick = () => {
    this.isOpen = !this.isOpen
    this.rerender()
  }

  onClearClick = () => {
    this.subscriber?.setAudienceFilter([])
    this.rerender()
  }

  clearFilter = (event: Event) => {
    event.preventDefault()
    this.selectedAudiences.splice(0, this.selectedAudiences.length)

    this.subscriber?.setAudienceFilter(this.selectedAudiences)
    this.isOpen = false
    this.rerender()
    MainModule.getInstance().hideModal(MODALS.AUDIENCE_DROPDOWN)
  }

  onAudiencesChange = (audiences: string[]) => {
    this.selectedAudiences = audiences
    this.rerender()
  }

  selectAudienceOption = (event: Event) => {
    const audienceButton = (event.target as Element)?.closest('.audience-chip') as Element | null
    const audienceId = audienceButton?.getAttribute('data-audience-id')

    const audienceIndex = this.selectedAudiences.findIndex(filter => filter === audienceId)
    if ((!audienceIndex || audienceIndex == -1) && audienceIndex !== 0) {
      this.selectedAudiences.push(audienceId as string)
    } else {
      this.selectedAudiences.splice(audienceIndex, 1)
    }

    this.subscriber?.setAudienceFilter(this.selectedAudiences)
  }

  getTextValue = () => {
    if (!this.selectedAudiences || !this.selectedAudiences.length) return
    return this.selectedAudiences
      .map(audience => {
        return audiences.find(a => a.id === audience)?.label
      })
      .join(', ')
  }

  renderAudienceFilters() {
    return `
      <lvc-dropdown modal-id=${MODALS.AUDIENCE_DROPDOWN} on-toggle="${this.toggleCb}">
        <lvc-search-filter-button
          slot="lvc-dropdown-trigger"
          count="${this.selectedAudiences.length}"
          ${this.isOpen ? 'selected' : ''}
          on-click="${this.clickCb}"
          on-clear="${this.clearCb}"
          data-last-child="true"
          data-value="${this.getTextValue()}"
        >
          ${Locale.getKey('audiencesFilter.audience')}
        </lvc-search-filter-button>
        <div
          class="audience-dropdown-content"
          slot="lvc-dropdown-content"
        >
          ${this.renderAudienceFiltersContent()}
        </div>
      </lvc-dropdown>
    `
  }

  renderMobileAudienceFilters() {
    return `
      <lvc-search-filter-mobile
        on-toggle="${this.drawerToggleCb}"
        ${this.isOpen ? `open="true"` : ''}
        data-count="${this.selectedAudiences.length}"
        title="${Locale.getKey('audiencesFilter.audience')}"
      >
        <lvc-search-filter-button
          slot="lvc-drawer-trigger"
          count="${this.selectedAudiences.length}" 
          on-click="${this.drawerClickCb}"
          on-clear="${this.clearCb}"
          data-value="${this.getTextValue()}"
        >
          ${Locale.getKey('audiencesFilter.audience')}
        </lvc-search-filter-button>
         <div
            class="audience-dropdown-content"
            slot="lvc-drawer-content"
          >
          ${this.renderAudienceFiltersContent()}
        </div>

      </lvc-search-filter-mobile>
      `
  }

  renderAudienceFiltersContent = () => {
    return (
      /* html */
      `
        <lvc-box padding="${window.matchMedia('(max-width: 730px)').matches ? '0' : '14px'}">
          <div class="header">
            <h4 class="disciplines-subtitle">
              ${Locale.getKey('audiencesFilter.audience')}
              ${
                this.selectedAudiences.length
                  ? `
                <div class="counter">${this.selectedAudiences.length}</div>
              `
                  : ''
              }
            </h4>
          </div>
          <lvc-box display="flex" flexWrap="wrap" flexDirection="column" rowGap="15px">
            ${audiences
              .map(
                audience =>
                  `
                <lvc-filter-chip
                  value=${audience.id}
                  class="category-chip audience-chip"
                  data-audience-id="${audience.id}"
                  selected="${this.selectedAudiences.includes(audience.id)}"
                >
                  ${audience.label}
                </lvc-filter-chip>
              `
              )
              .join('')}
          </lvc-box>
        </lvc-box>
      `
    )
  }

  html = () => {
    if (getVisibleWidth() < SCREEN_WIDTHS.DESKTOP_MEDIUM) {
      return this.renderMobileAudienceFilters()
    } else {
      return this.renderAudienceFilters()
    }
  }

  css = () => `
    h4 {
      margin: 0 0 4px 0;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      opacity: 0.3;
    }
    
    .header {
      padding-bottom: 20px;
      margin-bottom: 20px;
      border-bottom: 1px solid #1E007D4D;
    }

    .disciplines-subtitle {
      font-weight: ${Font.weight.bold};
      font-size: 16px;
      line-height: 150%;
      color: #1E007D;
      opacity: 1;
      display: flex;
      align-items: center;
      width: fit-content;
      position: relative;
    }
    
    .disciplines-subtitle .counter {
      border: 1px solid #1E007D;
      width: 30px;
      height: 30px;
      font-size: 12px;
      font-weight: 400;
      border-radius: 15px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      right: -6px;
      bottom: 6px;
      transform: translateX(100%);
    }

    lvc-drawer .audience-dropdown-content {
      background: transparent;
      box-shadow: none;
      border: none;
      width: auto;
      margin: auto;
    }

    lvc-drawer .disciplines-subtitle {
      display: none;
    }

    @media screen and (max-width: 991px) {

      .header {
        display: none;
      }
      
      #disciplines-content {
        max-height: calc(100vh - 72px - 73px - 84.5px - 10px);
        max-width: 100vw;
        overflow-y: scroll;
      }
    }
  `

  eventListeners: EventListeners = [
    ['.audience-chip', 'click', this.selectAudienceOption.bind(this)],
    [window, 'lvc:clear-search', this.clearFilter],
  ]
}

export const audiences = [
  {
    id: 'Général',
    label: Locale.getKey('audiencesFilter.audiences.general'),
  },
  {
    id: 'Tout-Petit',
    label: Locale.getKey('audiencesFilter.audiences.toddler'),
  },
  {
    id: 'Enfant',
    label: Locale.getKey('audiencesFilter.audiences.children'),
  },
  {
    id: 'Adolescent',
    label: Locale.getKey('audiencesFilter.audiences.teen'),
  },
  { id: 'Adulte', label: Locale.getKey('audiencesFilter.audiences.adult') },
  {
    id: 'Familial',
    label: Locale.getKey('audiencesFilter.audiences.family'),
  },
]

export const facetToOption = (facet: IFacetOption) => {
  switch (facet.value) {
    case 'General':
      return 'Général'
    case 'Toddler':
      return 'Tout-Petit'
    case 'Childhood':
      return 'Enfant'
    case 'Children':
      return 'Enfant'
    case 'Youths':
      return 'Adolescent'
    case 'Adult':
      return 'Adulte'
    case 'Elder':
      return 'Adulte'
    case 'Family':
      return 'Familial'
  }

  return ''
}

export const optionToFacet = (optionId: string): IFacetOption[] => {
  switch (optionId) {
    case 'Général':
      return [{ available: true, selected: true, value: 'General' }]
    case 'Tout-Petit':
      return [{ available: true, selected: true, value: 'Toddler' }]
    case 'Enfant':
      return [
        { available: true, selected: true, value: 'Childhood' },
        { available: true, selected: true, value: 'Children' },
      ]
    case 'Adolescent':
      return [{ available: true, selected: true, value: 'Youths' }]
    case 'Adulte':
      return [
        { available: true, selected: true, value: 'Adult' },
        { available: true, selected: true, value: 'Elder' },
      ]
    case 'Familial':
      return [{ available: true, selected: true, value: 'Family' }]
  }

  return []
}
